/**
 * Commerce components (spec 05-frontend): CreditChip, CostBadge,
 * CountdownTimer (server-expiry — no client drift), PromoBanner, OfferModal.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import type { PromotionDoc } from '@vidply/shared';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { ArrowUpRight, CreditIcon, Hourglass, X } from './icons';
import { Badge, Button } from './ui';

// ── Credits chip (top nav) ───────────────────────────────────────────────────

export function CreditChip() {
  const { user } = useAuth();
  const { data } = useQuery({ queryKey: ['credits'], queryFn: api.credits, enabled: !!user, staleTime: 30_000 });
  if (!user || !data) return null;
  return (
    <Link
      to="/account"
      className="flex items-center gap-1.5 rounded-full border border-border bg-surface-2 px-3 py-1.5 text-xs font-semibold text-credit transition-colors hover:border-border-strong"
      title={`${data.balances.available}`}
    >
      <CreditIcon className="size-3.5" />
      <span className="tabular">{data.balances.available.toLocaleString()}</span>
    </Link>
  );
}

// ── Cost badge under Generate buttons ───────────────────────────────────────

export function CostBadge({ credits, unlimited, discounted }: { credits: number | null; unlimited?: boolean; discounted?: number | null }) {
  const { t } = useTranslation();
  if (unlimited) return <Badge tone="accent">{t('create.unlimitedBadge')}</Badge>;
  if (credits === null) return null;
  return (
    <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-credit">
      {discounted != null && discounted !== credits && <s className="font-normal text-muted tabular">{credits}</s>}
      <CreditIcon className="size-4" />
      <span className="tabular">{discounted ?? credits}</span>
    </span>
  );
}

// ── Countdown (always server-anchored) ──────────────────────────────────────

export function useCountdown(endsAtIso: string | undefined, serverNowIso: string | undefined) {
  // Offset between server and client clocks, computed once per payload.
  const offset = useMemo(() => (serverNowIso ? new Date(serverNowIso).getTime() - Date.now() : 0), [serverNowIso]);
  const [remaining, setRemaining] = useState(0);
  useEffect(() => {
    if (!endsAtIso) return;
    const end = new Date(endsAtIso).getTime();
    const tick = () => setRemaining(Math.max(0, end - (Date.now() + offset)));
    tick();
    const h = setInterval(tick, 1000);
    return () => clearInterval(h);
  }, [endsAtIso, offset]);
  const s = Math.floor(remaining / 1000);
  return {
    expired: !!endsAtIso && remaining <= 0,
    days: Math.floor(s / 86400),
    hours: Math.floor((s % 86400) / 3600),
    minutes: Math.floor((s % 3600) / 60),
    seconds: s % 60,
  };
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

// ── Promo banner (yellow countdown band from the screenshots) ────────────────

export function PromoBanner() {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem('promo-dismissed') === '1');
  const { data } = useQuery({ queryKey: ['promotions'], queryFn: api.promotions, staleTime: 60_000 });
  const promo = data?.promotions.find((p) => p.type === 'countdown_offer');
  const cd = useCountdown(promo?.endsAt, data?.now);

  if (dismissed || !promo || cd.expired) return null;
  return (
    <div className="relative z-40 flex items-center justify-center gap-3 bg-banner px-4 py-1.5 text-xs font-semibold text-banner-text">
      <Hourglass className="size-3.5" aria-hidden />
      <span className="display tracking-wide">
        {t(promo.headlineKey)}{' '}
        <span className="tabular">
          {cd.days}D {pad(cd.hours)}H {pad(cd.minutes)}M {pad(cd.seconds)}S
        </span>
      </span>
      <span className="hidden text-banner-text/80 sm:inline">{promo.bodyKey ? t(promo.bodyKey) : null}</span>
      <Link to="/signup" className="inline-flex items-center gap-1 rounded-full bg-banner-text px-3 py-1 text-banner hover:opacity-90">
        {t(promo.ctaKey ?? 'nav.signup')}
        <ArrowUpRight className="size-3 icon-mirror" aria-hidden />
      </Link>
      <button
        aria-label={t('common.close')}
        className="absolute end-3 opacity-60 hover:opacity-100"
        onClick={() => {
          sessionStorage.setItem('promo-dismissed', '1');
          setDismissed(true);
        }}
      >
        <X className="size-4" />
      </button>
    </div>
  );
}

// ── Offer modal (signup discount w/ model chips + timer) ─────────────────────

export function OfferModal({ promo, serverNow, onClose }: { promo: PromotionDoc; serverNow: string; onClose: () => void }) {
  const { t } = useTranslation();
  const cd = useCountdown(promo.endsAt, serverNow);
  const { data: modelsData } = useQuery({ queryKey: ['models'], queryFn: () => api.models(), staleTime: 300_000 });
  const chips = (promo.modelChips ?? [])
    .map((id) => modelsData?.models.find((m) => m.id === id))
    .filter(Boolean);

  return (
    <div className="p-6 pt-10 text-center">
      <Badge tone="promo">{t('promo.limitedOffer')}</Badge>
      <h2 className="display mt-3 text-2xl leading-tight">
        <span className="text-accent">{t(promo.headlineKey)}</span>
      </h2>
      {promo.bodyKey && <p className="mt-2 text-sm text-muted">{t(promo.bodyKey)}</p>}

      {chips.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
          {chips.map((m) => (
            <span key={m!.id} className="flex items-center gap-1.5 rounded-full border border-border bg-surface-2 px-3 py-1 text-xs">
              <span className="size-2 rounded-full" style={{ background: m!.ui.accent }} aria-hidden />
              {m!.displayName}
            </span>
          ))}
        </div>
      )}

      <div className="mx-auto mt-5 max-w-sm rounded-2xl border border-promo/40 bg-promo/10 p-3">
        <p className="text-[11px] font-semibold text-promo">{t('promo.endsIn')}</p>
        <div className="mt-2 grid grid-cols-4 gap-2">
          {(
            [
              [cd.days, t('promo.days')],
              [cd.hours, t('promo.hours')],
              [cd.minutes, t('promo.minutes')],
              [cd.seconds, t('promo.seconds')],
            ] as const
          ).map(([v, label], i) => (
            <div key={i} className="rounded-xl bg-surface-2 py-2">
              <div className="tabular text-xl font-bold">{pad(v)}</div>
              <div className="text-[10px] text-muted">{label}</div>
            </div>
          ))}
        </div>
      </div>

      <Link to="/signup" onClick={onClose}>
        <Button variant="accent" size="lg" className="mt-5 w-full">
          {t(promo.ctaKey ?? 'nav.signup')}
        </Button>
      </Link>
    </div>
  );
}
